import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";

export default function CidAutocomplete({ value, onSelect, placeholder = "Buscar CID (código ou nome)", testid = "cid-autocomplete" }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = (text) => {
    setQ(text);
    onSelect && onSelect(text, null);
    clearTimeout(timer.current);
    if (!text || text.trim().length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/cid/search`, { params: { q: text, limit: 15 } });
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
  };

  const pick = (item) => {
    setQ(item.code);
    onSelect && onSelect(item.code, item.description);
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => search(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="pl-8"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {loading && <Loader2 className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-border bg-popover shadow-lg text-sm"
          data-testid={`${testid}-results`}>
          {results.map((it) => (
            <li key={it.code}>
              <button type="button" onClick={() => pick(it)}
                className="w-full text-left px-3 py-2 hover:bg-secondary flex gap-2 items-start"
                data-testid={`${testid}-option-${it.code}`}>
                <span className="font-mono-data font-semibold text-ai shrink-0">{it.code}</span>
                <span className="text-muted-foreground">{it.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && results.length === 0 && q.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg px-3 py-2 text-sm text-muted-foreground">
          Nenhum CID encontrado.
        </div>
      )}
    </div>
  );
}
